import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

const card = "rounded-xl border bg-black/60 p-5";
const cardStyle = { borderColor: "rgba(255, 16, 240, 0.25)", boxShadow: "0 0 24px -16px #FF10F0" };
const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-US"));
const ATHENS_FMT = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Athens", dateStyle: "medium", timeStyle: "short" });
export const formatAthens = (iso) => {
  const d = new Date(iso || "");
  return Number.isNaN(d.getTime()) ? "" : `${ATHENS_FMT.format(d)} (Athens)`;
};

const PLATFORM_LABELS = {
  twitter: "X", x: "X", instagram: "Instagram", tiktok: "TikTok", onlyfans_vip: "OnlyFans VIP",
  onlyfans_free: "OnlyFans Free", jff: "JustFor.Fans", rfc: "RawFuckClub", faphouse: "FapHouse",
};

// Social > Overview: GET /api/social/overview (read-only, admin-only). Shows the
// X queue, Instagram connection and the post tracker; never calls X or Instagram.
export default function SocialOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: body } = await api.get("/api/social/overview");
      setData(body);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load the social overview.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div role="alert" className="rounded-xl border border-red-900 bg-red-950/30 p-5 text-sm text-red-200">
        {error} <button type="button" onClick={load} className="ml-2 underline">Retry</button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-zinc-400" data-testid="social-overview-loading">Loading…</p>;

  const { x, instagram, tracker } = data;
  const platforms = Object.entries(tracker.by_platform || {});

  return (
    <div className="space-y-6" data-testid="social-overview">
      {data.empty && (
        <div data-testid="social-overview-empty" className={card} style={cardStyle}>
          <p className="text-sm text-zinc-300">Nothing here yet. Draft a post in the X Composer or log one in the tracker, and it will show up here.</p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <section className={card} style={cardStyle}>
          <p className="text-xs uppercase tracking-widest text-pink-300">Total followers</p>
          <p className="mt-2 text-3xl font-bold text-white" data-testid="social-total-followers">{fmt(data.total_followers)}</p>
          <p className="mt-1 text-xs text-zinc-500">X counted once{x.followers_source ? ` (${x.followers_source.split(":")[0]})` : ""}.</p>
        </section>

        <section className={card} style={cardStyle}>
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-pink-300">X</p>
            <Link to="/crm/x" className="text-xs text-pink-300 hover:text-pink-200">Composer ↗</Link>
          </div>
          <p className="mt-2 text-3xl font-bold text-white" data-testid="social-x-followers">{fmt(x.followers)}</p>
          <p className="text-xs text-zinc-500">followers</p>
          <dl className="mt-3 grid grid-cols-3 gap-2 text-center text-xs" data-testid="social-x-queue">
            <div><dt className="text-zinc-500">Drafts</dt><dd className="text-lg text-white">{x.queue.draft || 0}</dd></div>
            <div><dt className="text-zinc-500">Queued</dt><dd className="text-lg text-white">{x.queue.pending || 0}</dd></div>
            <div><dt className="text-zinc-500">Failed</dt><dd className={`text-lg ${x.queue.failed ? "text-red-300" : "text-white"}`}>{x.queue.failed || 0}</dd></div>
          </dl>
          <p className="mt-3 text-xs text-zinc-400" data-testid="social-x-next">
            {x.next_post ? <>Next: {formatAthens(x.next_post.scheduled_at)} · “{x.next_post.preview}”</> : "No post queued."}
          </p>
        </section>

        <section className={card} style={cardStyle}>
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-pink-300">Instagram</p>
            <Link to="/crm/social?tab=instagram" className="text-xs text-pink-300 hover:text-pink-200">Open ↗</Link>
          </div>
          <p className="mt-2 text-3xl font-bold text-white" data-testid="social-ig-followers">{fmt(instagram.followers)}</p>
          <p className="text-xs text-zinc-500">followers{instagram.followers === null ? " (not recorded yet)" : ""}</p>
          <p className="mt-3 text-xs" data-testid="social-ig-status">
            {instagram.publishing && instagram.publishing !== "not_connected"
              ? <span className="text-emerald-300">Connected with Instagram Login.</span>
              : instagram.configured
                ? <span className="text-amber-300">Old token saved. Reconnect with Instagram Login in the Instagram tab.</span>
                : <span className="text-amber-300">Not connected. Connect with Instagram Login in the Instagram tab.</span>}
          </p>
          <p className="mt-1 text-xs text-zinc-500" data-testid="social-ig-publishing">
            {instagram.publishing === "enabled" ? "Publishing is on. Posts go out only after you approve them."
              : instagram.publishing === "paused" ? "Publishing is paused on the server (IG_PUBLISH_ENABLED)."
                : "Publishing is not connected yet."}
          </p>
        </section>
      </div>

      <section className={card} style={cardStyle} data-testid="social-tracker-summary">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Post tracker</h3>
          <Link to="/crm/social?tab=tracker" className="text-xs text-pink-300 hover:text-pink-200">Open tracker ↗</Link>
        </div>
        <p className="mt-1 text-xs text-zinc-400">{fmt(tracker.total)} posts logged · {fmt(tracker.posted_last_7_days)} posted in the last 7 days (Athens).</p>
        {platforms.length ? (
          <ul className="mt-3 flex flex-wrap gap-2">
            {platforms.map(([p, n]) => (
              <li key={p} className="rounded-full border border-[#FF10F0]/30 px-3 py-1 text-xs text-zinc-200">{PLATFORM_LABELS[p] || p}: {fmt(n)}</li>
            ))}
          </ul>
        ) : <p className="mt-3 text-xs text-zinc-500">No posts logged yet.</p>}
      </section>
    </div>
  );
}
